// --- UTILS & API ---
const API_URL = 'https://ops.purelyorganic.pk/api/api.php';

// JWT helpers — token stored in localStorage after loginPin
const getJwt  = ()          => localStorage.getItem('pos_jwt');
const setJwt  = (token)     => localStorage.setItem('pos_jwt', token);
const clearJwt = ()         => localStorage.removeItem('pos_jwt');

let _on401 = null; // App.jsx registers a handler to force re-login on token expiry
const setOn401Handler = (fn) => { _on401 = fn; };

const apiCall = async (action, payload = null) => {
    // Shared hosting occasionally drops connections outright for a second or two
    // (ERR_CONNECTION_CLOSED) when its concurrent-process cap is hit. A read that
    // fails at the NETWORK level (fetch throws — no HTTP response at all) is safe
    // to retry: GETs are idempotent. Mutations (POST) are never auto-retried —
    // a retry after an ambiguous failure could double-write an order or payment.
    const maxAttempts = payload ? 1 : 3;
    for (let attempt = 1; ; attempt++) {
        try {
            const jwt = getJwt();
            const headers = {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store',
                'Pragma': 'no-cache',
            };
            // Public actions (loginPin, sendAdminOTP, …) need no token — the server's
            // public_actions list bypasses auth. Never ship a static credential to the client.
            if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
            const options = { method: payload ? 'POST' : 'GET', headers, credentials: 'same-origin', cache: 'no-store' };
            if (payload) options.body = JSON.stringify({ action, ...payload });
            const url = payload ? API_URL : `${API_URL}?action=${action}&_t=${Date.now()}`;
            const res = await fetch(url, options);
            if (res.status === 401) {
                clearJwt();
                if (_on401) _on401();
                return { error: 'Session expired — please log in again' };
            }
            return await res.json();
        } catch (err) {
            if (attempt >= maxAttempts) return { error: err.message };
            // 500ms then 1500ms, with jitter so a fleet of terminals that failed
            // together doesn't retry in lockstep and re-trip the server cap
            await new Promise(r => setTimeout(r, attempt * 500 + attempt * Math.random() * 500));
        }
    }
};

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);
const formatPrice = (price) => {
    const n = parseFloat(price);
    return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(isNaN(n) ? 0 : n);
};
const formatDate = (dateStr) => { 
    if (!dateStr) return 'Just now'; 
    return new Date(dateStr.replace(' ', 'T')).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); 
};

const compressImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader(); reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image(); img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = 500 / img.width; canvas.width = 500; canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
    };
  });
};

const base64ToFile = async (base64, filename) => { 
    const res = await fetch(base64); const blob = await res.blob(); 
    return new File([blob], filename, { type: 'image/jpeg' }); 
};

// --- HTML escaping for text interpolated into print templates ---
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
    c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c]);

// --- SANDBOXED PRINT PIPELINE ---
// All printing goes through a hidden sandboxed iframe WITHOUT allow-scripts:
// print templates interpolate customer/product names into raw HTML, so a
// crafted name like <img onerror=...> would otherwise execute with app origin
// (and could read the JWT from localStorage). The sandbox makes script
// execution impossible in the print document, whatever the templates contain.
const printFullHtml = (fullHtml, delayMs = 500) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    doc.open(); doc.write(fullHtml); doc.close();
    // Delay lets stylesheets/fonts load before the print snapshot is taken
    setTimeout(() => {
        try { frame.contentWindow.focus(); frame.contentWindow.print(); }
        catch (e) { console.error('Print failed:', e); }
        // Remove well after the dialog closes; Chrome snapshots at print() time
        setTimeout(() => frame.remove(), 60000);
    }, delayMs);
};

const printHtmlDiv = (divId) => {
    const content = document.getElementById(divId).innerHTML;
    printFullHtml('<html><head><title>Print</title><style>body{font-family:sans-serif; padding:20px;}</style></head><body>' + content + '</body></html>', 500);
};

const printRawHtml = (htmlContent, opts = {}) => {
    const isUrdu   = opts.lang === 'ur';
    const fontLink = isUrdu
        ? '<link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap" rel="stylesheet">'
        : '';
    const bodyFont = isUrdu ? "'Noto Nastaliq Urdu', serif" : 'monospace';
    const dir      = isUrdu ? ' dir="rtl"' : '';
    // Extra delay when Urdu so the font loads before the print dialog opens
    printFullHtml(
        `<html${dir}><head><title>Document</title>${fontLink}<style>body{font-family:${bodyFont};padding:20px;direction:${isUrdu?'rtl':'ltr'};}</style></head><body>` + htmlContent + '</body></html>',
        isUrdu ? 1200 : 500
    );
};

const printBarcodeDiv = (divId) => {
    const el = document.getElementById(divId);
    if (!el) return;
    // Serialize SVG properly so it renders in the print document
    const svgEl = el.querySelector('svg');
    let svgHtml = '';
    if (svgEl) {
        const serializer = new XMLSerializer();
        svgHtml = serializer.serializeToString(svgEl);
    }
    const titleEl = el.querySelector('.print-title');
    const priceEl = el.querySelector('.print-price');
    const titleText = escapeHtml(titleEl ? titleEl.innerText : '');
    const priceText = escapeHtml(priceEl ? priceEl.innerText : '');

    printFullHtml(`
        <html><head><title>Label</title>
        <style>
            @media print {
                @page { size: 1.5in 1in; margin: 0; }
                body { margin: 0; padding: 2px; background: white; }
            }
            body { font-family: Arial, sans-serif; text-align: center; background: white; margin: 0; padding: 2px; }
            .wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 1.5in; min-height: 1in; }
            .title { font-size: 12px; font-weight: 900; max-width: 1.44in; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 1px; }
            .price { font-size: 20px; font-weight: 900; margin-bottom: 2px; }
            svg { width: 1.44in; height: auto; max-height: 0.4in; display: block; margin: 0 auto; }
        </style>
        </head><body>
        <div class="wrap">
            <div class="title">${titleText}</div>
            <div class="price">${priceText}</div>
            ${svgHtml}
        </div>
        </body></html>
    `, 400);
};

export {
    API_URL,
    getJwt,
    setJwt,
    clearJwt,
    setOn401Handler,
    apiCall,
    generateId,
    formatPrice,
    formatDate,
    compressImage,
    base64ToFile,
    escapeHtml,
    printFullHtml,
    printHtmlDiv,
    printRawHtml,
    printBarcodeDiv
};
